import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'

interface Organization {
  id: number
  name: string
  description?: string
  /** 'startup' | 'agency' */
  orgType?: string
  createdAt: string
  updatedAt: string
  projectCount: number
  memberCount: number
  requestCount: number
  ownerName?: string
  ownerEmail?: string
  riskIntelligenceEnabled?: boolean
  v0PretestEnabled?: boolean
  /** Onboarding flow: 'scope' | 'startup' | 'plan' | 'reshuffle' | 'riskintelligence' | null */
  onboardingSource?: string | null
}

interface OrganizationStats {
  totalOrganizations: number
  activeOrganizations: number
  organizationsWithProjects: number
  avgProjectsPerOrg: number
  avgMembersPerOrg: number
}

interface OrganizationProject {
  id: number
  title: string
  description?: string
  status: string
  createdAt: string
  updatedAt: string
  memberCount: number
  requestCount: number
}

interface OrganizationMember {
  id: number
  name: string
  email: string
  is_connected: boolean
  role_id: number
  role_name: string
  project_title: string
  project_id: number
  joined_at: string
}

interface PaginatedResponse<T> {
  data: T[]
  meta: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export const organizationsApi = createApi({
  reducerPath: 'organizationsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/organizations',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Organization', 'OrganizationStats'],
  endpoints: (builder) => ({
    getOrganizations: builder.query<PaginatedResponse<Organization>, { page?: number; limit?: number; search?: string }>({
      query: ({ page = 1, limit = 20, search }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        })
        if (search) params.append('search', search)
        return `/?${params}`
      },
      transformResponse: (response: any) => ({
        data: response.data,
        meta: response.meta
      }),
      providesTags: ['Organization'],
    }),
    getOrganizationById: builder.query<Organization, number>({
      query: (id) => `/${id}`,
      transformResponse: (response: any) => response.data,
      providesTags: ['Organization'],
    }),
    getOrganizationStats: builder.query<OrganizationStats, void>({
      query: () => '/stats',
      transformResponse: (response: any) => response.data,
      providesTags: ['OrganizationStats'],
    }),
    getOrganizationProjects: builder.query<PaginatedResponse<OrganizationProject>, { id: number; page?: number; limit?: number }>({
      query: ({ id, page = 1, limit = 10 }) => `/${id}/projects?page=${page}&limit=${limit}`,
      transformResponse: (response: any) => ({
        data: response.data,
        meta: response.meta
      }),
      providesTags: ['Organization'],
    }),
    getOrganizationMembers: builder.query<PaginatedResponse<OrganizationMember>, { id: number; page?: number; limit?: number }>({
      query: ({ id, page = 1, limit = 10 }) => `/${id}/members?page=${page}&limit=${limit}`,
      transformResponse: (response: any) => ({
        data: response.data,
        meta: response.meta
      }),
      providesTags: ['Organization'],
    }),
    updateOrganization: builder.mutation<Organization, { id: number; data: Partial<Organization> }>({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: any) => response.data,
      invalidatesTags: ['Organization', 'OrganizationStats'],
    }),
    deleteOrganization: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Organization', 'OrganizationStats'],
    }),
    inviteOrganizationAdmin: builder.mutation<{ email: string; inviteUrl: string }, number>({
      query: (id) => ({
        url: `/${id}/invite-admin`,
        method: 'POST',
      }),
      transformResponse: (response: any) => response.data,
    }),
  }),
})

export const {
  useGetOrganizationsQuery,
  useGetOrganizationByIdQuery,
  useGetOrganizationStatsQuery,
  useGetOrganizationProjectsQuery,
  useGetOrganizationMembersQuery,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useInviteOrganizationAdminMutation,
} = organizationsApi 